import { BaseModel } from '@fry/lib/store/base-model';
import { DBDoc } from '@fry/lib/store/store.interface';

import { WalkthroughStep, WalkthroughStepJSON } from './walkthrough-step';
import { WalkthroughState } from './walkthrough-state';

export interface WalkthroughJSON extends DBDoc {
    walkthrough: WalkthroughStepJSON;
}

export class Walkthrough extends BaseModel<WalkthroughJSON> {
    description: string;
    generatedAt: string;
    hidden: boolean;
    id: string;
    state: WalkthroughState;

    // Overall Walkthrough progress is always numerical, steps on other hand
    // can return complex progress.
    get progress(): number {
        const numberOfSteps = this.steps.length;
        if (numberOfSteps === 0) { return 0.0; }

        const sum = this.steps.reduce((acc, step) => {
            if (typeof step.progress === 'number') {
                return acc += step.progress;
            } else {
                return acc += step.progress.actual / step.progress.expected;
            }
        }, 0.0);
        return sum / numberOfSteps;
    }

    steps: WalkthroughStep[] = [];
    title: string;

    public constructor(doc: WalkthroughJSON) {
        super(doc);

        this.id = doc.walkthrough.id;
        this.description = doc.walkthrough.description;
        this.title = doc.walkthrough.title;

        if (doc.walkthrough.steps !== null ||
            doc.walkthrough.steps !== undefined) {
            this.steps = doc.walkthrough.steps.map(step => {
                return WalkthroughStep.stepFromJSON(step);
            });
        }
    }

    public firstStep(): WalkthroughStep {
        if (this.hasNoSteps) { throw new Error($localize `Walkthrough has no steps!`); }

        return this.steps[0];
    }

    public lastStep(): WalkthroughStep {
        if (this.hasNoSteps) { throw new Error($localize `Walkthrough has no steps!`); }

        return this.steps[this.steps.length - 1];
    }

    public currentStep(): WalkthroughStep {
        if (this.hasNoSteps) { throw new Error($localize `Walkthrough has no steps!`); }

        const done = this.steps.filter(step => step.done);
        if (done.length === 0) { return this.firstStep(); }
        return done[done.length - 1];
    }

    public nextStep(): WalkthroughStep {
        if (this.hasNoSteps) { throw new Error($localize `Walkthrough has no steps!`); }

        const current = this.currentStep();
        if (!current.done) { return current; }

        const index = this.steps.indexOf(current);
        if (index === -1) { throw new Error($localize `Invalid current step!`); }

        if (index + 1 === this.steps.length) { return this.lastStep(); }
        return this.steps[index + 1];
    }

    private get hasNoSteps(): boolean {
        if (!this.steps) { return true; }
        return this.steps.length === 0;
    }

    //
    // State management
    //

    public applyState(state: WalkthroughState): void {
        if (this.id !== state.id) {
            throw new Error($localize `State is not for this Walkthrough!`);
        }

        this.state = state;
        this.generatedAt = state.generatedAt;
        this.hidden = state.hidden;

        if (!this.hasNoSteps) {
            if (!state.state.steps) {
                throw new Error($localize `State has no steps but walkthrough does!`);
            }
            if (this.steps.length !== state.state.steps.length) {
                throw new Error($localize `Walkthrough has ${this.steps.length} steps but State has ${state.state.steps.length}!`);
            }
            this.steps.forEach((step, index) => {
                step.applyState(state.state.steps[index]);
            });
        }
    }
}
